import { Component } from 'react';
import { Button, Form } from "react-bootstrap";
import GameData from "../mobx/GameData";
import GameplayStyle from "../data/GameplayStyle";
import PlayerSpeed from '../data/PlayerSpeed';
import { observe } from 'mobx';
import ApplicationState from "../mobx/ApplicationState";
import { coerceUnknownFields } from '../data/BaseGame';

const allFields = ['gameTitle', 'author', 'coinsCollectedText', 'cratesRemovedText'];

export default class GameSettingsPage extends Component {

    thisId = null;
    gameDataCleanup = null;

    // eslint-disable-next-line no-useless-constructor
    constructor(props) {
        super(props);

        this.state = {
            formSubmittedOnce: false
        }
    }

    componentDidMount() {
        this.gameDataCleanup = observe(GameData, () => { 
            this.forceUpdate()
        });
    }

    componentWillUnmount() {
        if (this.gameDataCleanup) {
            this.gameDataCleanup();
        }
    }

    getFeedbackData(field) {
        const fieldElem = document.getElementById(field);
        if (!fieldElem || !this.state.formSubmittedOnce) {
            return {valid: 'indeterminate', feedback: ''};
        }
        const fieldValue = fieldElem.value;

        switch(field) {

            case 'gameTitle':
                if (fieldValue.length === 0) {
                    return {valid: false, feedback: 'A value is required'};
                } else if (fieldValue.length > 26) {
                    return {valid: false, feedback: 'Game title cannot be more than 26 characters. (' + fieldValue.length + ')'}
                } else if (GameData.userGames.find(a => (a.title === fieldValue && a.id !== GameData.currentGame.id))) {
                    return {valid: false, feedback: 'Cannot have two games with the same title!'};
                }
                break;
            case 'author':
                if (fieldValue.length === 0) {
                    return {valid: false, feedback: 'A value is required'};
                } else if (fieldValue.length > 64) {
                    return {valid: false, feedback: 'Author name cannot be longer than 64 characters'};
                }
                break;
            case 'coinsCollectedText':
            case 'cratesRemovedText':
                if (fieldValue.length === 0) {
                    return {valid: false, feedback: 'A value is required'};
                } else if (fieldValue.length > 19) {
                    return {valid: false, feedback: 'This text cannot be longer than 19 characters'};
                }
                break;
            default:
        }

        // If you make it out, we don't care about your field. Carry on.
        return {valid: true, feedback: ''};
    }

    fieldChanged() {
        this.forceUpdate();
        ApplicationState.setCurrentPageUnsaved();
    }
    
    async saveChanges(event) {
        if (event && event.preventDefault) {
            event.preventDefault();
        }

        // Need this field to be updated before we can do other calculations.
        await new Promise(resolve => this.setState({formSubmittedOnce: true}, resolve));
        let isValid = true;
        allFields.forEach(field => {
            if (!this.getFeedbackData(field).valid) {
                isValid = false;
            }
        });

        if (!isValid) {
            ApplicationState.addAlert('danger', 'There are errors with one or more fields on the page, saving failed.');
            console.warn('One or more fields is invalid, bailing out of game update.');
            return;
        }

        let updatedGame = {
            ...coerceUnknownFields(GameData.currentGame),
            title: document.getElementById('gameTitle').value,
            author: document.getElementById('author').value,
            defaultLevelStyle: parseInt(document.getElementById('gameplayStyle').value, 10),
            playerSpeed: parseInt(document.getElementById('playerSpeed').value, 10),
            cratesRemovedText: document.getElementById('cratesRemovedText').value.padEnd(19, ' '),
            coinsCollectedText: document.getElementById('coinsCollectedText').value.padEnd(19, ' '),
            showLevelEnabled: document.getElementById('showLevelEnabled').checked,
            undoEnabled: document.getElementById('undoEnabled').checked,
            showKeyCount: document.getElementById('showKeyCount').checked,
            disableStatsScreen: document.getElementById('disableStatsScreen').checked,
            smallRom: false,
            animateBlockMovement: document.getElementById('animateBlockMovement').checked

        };

        GameData.replaceCurrentGame(updatedGame);

        // SUCCESS! Share the update!
        ApplicationState.addAlert('success', 'Game details updated successfully.');
        ApplicationState.unsetCurrentPageUnsaved();
        
    }

    render() {
        const currentGame = coerceUnknownFields(GameData.currentGame);
        
        let gameplayStyle = GameData.currentGame.defaultLevelStyle;

        if (document.getElementById('gameplayStyle')) {
            gameplayStyle = parseInt(document.getElementById('gameplayStyle').value, 10);
        } 

        return (
            <div className="col-lg-8 mx-auto">
                <h1>Game Details</h1>

                <p>Configure some basic settings for your game here.</p>

                <Form noValidate ref={this.form} onSubmit={event => this.saveChanges(event)}>
                    <Form.Group className="mb-3" controlId="gameTitle">
                        <Form.Label>Game Title</Form.Label>
                        <Form.Control required isValid={this.getFeedbackData('gameTitle').valid === true} isInvalid={this.getFeedbackData('gameTitle').valid === false} type="text" placeholder="Enter a game title!" defaultValue={currentGame.title} onChange={() => this.fieldChanged()} />
                        <Form.Control.Feedback type="invalid">{this.getFeedbackData('gameTitle').feedback}</Form.Control.Feedback>
                        <Form.Text as="small" className="text-muted">Max 26 characters</Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="author">
                        <Form.Label>Author</Form.Label>
                        <Form.Control required isValid={this.getFeedbackData('author').valid === true} isInvalid={this.getFeedbackData('author').valid === false} type="text" placeholder="Enter your name!" defaultValue={currentGame.author} onChange={() => this.fieldChanged()} />
                        <Form.Control.Feedback type="invalid">{this.getFeedbackData('author').feedback}</Form.Control.Feedback>
                        <Form.Text as="small" className="text-muted">Names longer than 26 characters may appear unusually</Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label htmlFor="gameplayStyle">Default Gameplay Style</Form.Label>
                        <Form.Select arial-label="Select a gameplay style" onChange={() => this.fieldChanged()} defaultValue={currentGame.defaultLevelStyle} id="gameplayStyle">
                            {Object.keys(GameplayStyle).map(name => {
                                return <option key={name} value={GameplayStyle[name]}>{name.replace(/_/g, ' ').toLowerCase()}</option>
                            })}
                        </Form.Select>
                        <Form.Text as="small" className="text-muted">This will affect any levels that you have not explicitly picked a gameplay style for.</Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label htmlFor="playerSpeed">Player movement speed</Form.Label>
                        <Form.Select arial-label="Select a movement speed" onChange={() => this.fieldChanged()} defaultValue={currentGame.playerSpeed} id="playerSpeed">
                            {Object.keys(PlayerSpeed).map(name => {
                                return <option key={name} value={PlayerSpeed[name]}>{name.replace(/_/g, ' ').toLowerCase()}</option>
                            })}
                        </Form.Select>
                        <Form.Text as="small" className="text-muted">This will affect any levels that you have not explicitly picked a gameplay style for.</Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="cratesRemovedText" style={{display: gameplayStyle === GameplayStyle.FILL_HOLES_WITH_CRATES ? 'block' : 'none'}}>
                        <Form.Label>Credits statistic text</Form.Label>
                        <Form.Control required isValid={this.getFeedbackData('cratesRemovedText').valid === true} isInvalid={this.getFeedbackData('cratesRemovedText').valid === false} type="text" placeholder="Text to show on credits screen" defaultValue={currentGame.cratesRemovedText.trim()} onChange={() => this.fieldChanged()} name="cratesRemovedText" />
                        <Form.Control.Feedback type="invalid">{this.getFeedbackData('cratesRemovedText').feedback}</Form.Control.Feedback>
                        <Form.Text as="small" className="text-muted">This will be shown on the credits screen next a count of how many "crates" you pushed into holes. Max 19 characters</Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="coinsCollectedText" style={{display: gameplayStyle === GameplayStyle.COLLECT_ALL_COINS ? 'block' : 'none'}}>
                        <Form.Label>Credits statistic text</Form.Label>
                        <Form.Control required isValid={this.getFeedbackData('coinsCollectedText').valid === true} isInvalid={this.getFeedbackData('coinsCollectedText').valid === false} type="text" placeholder="Text to show on credits screen" defaultValue={currentGame.coinsCollectedText.trim()} onChange={() => this.fieldChanged()} name="coinsCollectedText" />
                        <Form.Control.Feedback type="invalid">{this.getFeedbackData('coinsCollectedText').feedback}</Form.Control.Feedback>
                        <Form.Text as="small" className="text-muted">This will be shown on the credits screen next a count of how many "coins" you collected. Max 19 characters</Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Check type="checkbox" id="undoEnabled" label="Allow Undo with B button" defaultChecked={currentGame.undoEnabled} name="undoEnabled" onChange={() => this.fieldChanged()} />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Check type="checkbox" id="showLevelEnabled" label="Show current level at top of screen" defaultChecked={currentGame.showLevelEnabled} name="showLevelEnabled" onChange={() => this.fieldChanged()} />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Check type="checkbox" id="showKeyCount" label="Show count of held keys at the top of the screen" defaultChecked={currentGame.showKeyCount} name="showKeyCount" onChange={() => this.fieldChanged()} />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                        <Form.Check type="checkbox" id="animateBlockMovement" label="Animate when blocks are pushed around the screen." defaultChecked={currentGame.animateBlockMovement} name="animateBlockMovement" onChange={() => this.fieldChanged()} />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Check type="checkbox" id="disableStatsScreen" label="Do not show the stats screen on game completion" defaultChecked={currentGame.disableStatsScreen} name="disableStatsScreen" onChange={() => this.fieldChanged()} />
                    </Form.Group>



                    <div className="button-footer-bar">
                        <Button variant="primary" type="submit" onClick={event => this.saveChanges(event)}>Save Changes</Button>
                    </div>
                </Form>


            </div>
        );
    }
}